import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import LegacyChannel from '@/components/legacy/LegacyChannel';
import { LegacyProjects } from '@/components/legacy/LegacyProjects';
import { WitnessWall } from '@/components/legacy/WitnessWall';
import { WitnessCircle } from '@/components/legacy/WitnessCircle';
import { LegacyCalendar } from '@/components/legacy/LegacyCalendar';
import { LegacyLab } from '@/components/legacy/LegacyLab';
import { LegacyPrayer } from '@/components/legacy/LegacyPrayer';
import { LegacyResources } from '@/components/legacy/LegacyResources';
import { LegacySisters } from '@/components/legacy/LegacySisters';
import {
  Crown, Calendar, Video, ExternalLink, Heart, MessageCircle,
  Sparkles, Pin, Send, BookOpen, Compass, Sun, Users, Eye, Trash2
} from 'lucide-react';

const API_URL = window.location.origin;

const SEASONS_JOURNEY = [
  { num: 1, name: 'Remember', months: 'Months 1–3', question: 'Who am I?', description: 'Before we build, we remember. This season invites each woman to reconnect with the truth of who she is beneath achievement, expectation, responsibility, and performance.' },
  { num: 2, name: 'Return', months: 'Months 4–6', question: 'How do I live from wholeness?', description: 'Knowing who we are is not enough. We must create lives, relationships, and rhythms that allow us to remain connected to ourselves.' },
  { num: 3, name: 'Build', months: 'Months 7–9', question: 'What am I called to create?', description: 'This season turns identity and purpose into action. Members begin building with greater clarity, courage, support, and alignment.' },
  { num: 4, name: 'Multiply', months: 'Months 10–12', question: 'How will my life and work continue beyond me?', description: 'Legacy is not only what we create. It is what we transfer, teach, protect, and make possible for others.' }
];

const GatheringCard = ({ info, icon: Icon, accent }) => (
  <Card className="card-base">
    <CardContent className="p-5">
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${accent}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-semibold text-forest">{info?.title || 'To be announced'}</p>
          {info?.date ? (
            <p className="text-sm text-gray-600 mt-0.5">
              {info.date}{info.time ? ` · ${info.time}` : ''}{info.timezone ? ` ${info.timezone}` : ''}
            </p>
          ) : (
            <p className="text-sm text-gray-400 mt-0.5">Date coming soon</p>
          )}
          {info?.description && <p className="text-sm text-gray-500 mt-1">{info.description}</p>}
          {info?.zoom_link && (
            <a href={info.zoom_link} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="mt-3 bg-forest hover:bg-forest/90 text-white" data-testid={`join-${(info.title || 'gathering').toLowerCase().replace(/\s+/g, '-')}`}>
                <Video className="h-4 w-4 mr-2" /> Join on Zoom
              </Button>
            </a>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const AnnouncementCard = ({ announcement, user, onUpdate }) => {
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const reacted = (announcement.reactions || []).includes(user?.id);

  const handleReact = async () => {
    try {
      await axios.post(`${API_URL}/api/legacy/announcements/${announcement.id}/react`);
      onUpdate();
    } catch {
      toast.error('Failed to react');
    }
  };

  const handleComment = async () => {
    if (!commentText.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/legacy/announcements/${announcement.id}/comments`, { content: commentText });
      setCommentText('');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to comment');
    } finally {
      setSubmitting(false);
    }
  };

  const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API_URL}/api/legacy/announcements/${announcement.id}/comments/${commentId}`);
      onUpdate();
    } catch {
      toast.error('Failed to delete comment');
    }
  };

  return (
    <Card className="card-base" data-testid={`legacy-announcement-${announcement.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {announcement.pinned && (
                <Badge className="bg-terracotta/10 text-terracotta"><Pin className="h-3 w-3 mr-1" /> Pinned</Badge>
              )}
              <h3 className="font-heading text-lg font-semibold text-forest">{announcement.title}</h3>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {announcement.author_name} · {new Date(announcement.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <p className="text-gray-700 mt-3 whitespace-pre-wrap">{announcement.content}</p>
        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-sand">
          <button
            onClick={handleReact}
            className={`flex items-center gap-1 text-sm transition-colors ${reacted ? 'text-rose-500' : 'text-gray-400 hover:text-rose-500'}`}
            data-testid={`announcement-react-${announcement.id}`}
          >
            <Heart className={`h-4 w-4 ${reacted ? 'fill-rose-500' : ''}`} />
            {(announcement.reactions || []).length > 0 && (announcement.reactions || []).length}
          </button>
          {announcement.comments_enabled !== false && (
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-forest transition-colors"
              data-testid={`announcement-comments-toggle-${announcement.id}`}
            >
              <MessageCircle className="h-4 w-4" />
              {(announcement.comments || []).length} {(announcement.comments || []).length === 1 ? 'Comment' : 'Comments'}
            </button>
          )}
        </div>
        {showComments && announcement.comments_enabled !== false && (
          <div className="mt-3 space-y-3">
            {(announcement.comments || []).map(c => (
              <div key={c.id} className="flex gap-2 group">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={c.author_avatar || undefined} />
                  <AvatarFallback className="bg-forest text-white text-xs">{c.author_name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="bg-sand/50 rounded-xl px-3 py-2 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium text-forest">{c.author_name}</p>
                    {(c.author_id === user?.id || isAdminUser) && (
                      <button onClick={() => handleDeleteComment(c.id)} className="text-gray-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`announcement-comment-delete-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{c.content}</p>
                </div>
              </div>
            ))}
            <div className="flex gap-2">
              <Input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleComment()}
                placeholder="Add an encouraging word..."
                className="text-sm"
                data-testid={`announcement-comment-input-${announcement.id}`}
              />
              <Button size="sm" onClick={handleComment} disabled={submitting || !commentText.trim()} className="bg-forest hover:bg-forest/90 text-white" data-testid={`announcement-comment-submit-${announcement.id}`}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const HOME_CACHE_KEY = 'legacy_home_cache_v1';
const readHomeCache = () => {
  try { return JSON.parse(sessionStorage.getItem(HOME_CACHE_KEY)); } catch { return null; }
};

export default function LegacyCircleHubPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [home, setHome] = useState(readHomeCache);
  const [announcements, setAnnouncements] = useState([]);
  const [recentWins, setRecentWins] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(() => !readHomeCache());
  const activeTab = searchParams.get('tab') || 'home';

  const fetchHome = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/home`);
      setHome(res.data);
      try { sessionStorage.setItem(HOME_CACHE_KEY, JSON.stringify(res.data)); } catch { /* noop */ }
    } catch (error) {
      console.error('Failed to load Legacy Circle:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/legacy/announcements`);
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Failed to load announcements:', error);
    }
  };

  useEffect(() => {
    fetchHome();
    fetchAnnouncements();
    axios.get(`${API_URL}/api/legacy/channels/wins`)
      .then(res => setRecentWins(res.data.posts || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/legacy/seasons`)
      .then(res => setSeasons(res.data.seasons || []))
      .catch(() => {});
  }, []);

  const settings = home?.settings || {};
  const season = settings.season_info || SEASONS_JOURNEY[0];
  const reflection = settings.current_reflection || {};

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-sand rounded-xl animate-pulse" />)}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="legacy-circle-hub">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-terracotta mb-3">
            <Crown className="h-5 w-5" />
            <span className="text-xs uppercase tracking-[0.25em] font-medium">Invitation Only</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-forest">Legacy Circle</h1>
          <p className="text-gray-600 mt-4 max-w-xl mx-auto italic" data-testid="legacy-core-promise">
            {settings.core_promise || 'Remember who you are. Become her fully. Build the legacy only she can create.'}
          </p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <Badge className="bg-forest/10 text-forest" data-testid="legacy-current-season">
              Season {settings.current_season || 1}: {settings.season_name || season.name}
            </Badge>
            {settings.monthly_theme && (
              <Badge variant="outline" className="text-gray-600">{settings.monthly_theme}</Badge>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })}>
          <TabsList className="flex flex-wrap w-full mb-8 h-auto gap-1">
            <TabsTrigger value="home" data-testid="legacy-tab-home">Home</TabsTrigger>
            <TabsTrigger value="reflection" data-testid="legacy-tab-reflection">Reflection</TabsTrigger>
            <TabsTrigger value="wins" data-testid="legacy-tab-wins">Wins</TabsTrigger>
            <TabsTrigger value="wisdom" data-testid="legacy-tab-wisdom">Wisdom</TabsTrigger>
            <TabsTrigger value="projects" data-testid="legacy-tab-projects">Journey</TabsTrigger>
            <TabsTrigger value="witness" data-testid="legacy-tab-witness">Witness</TabsTrigger>
            <TabsTrigger value="circle" data-testid="legacy-tab-circle">My Circle</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="legacy-tab-calendar">Calendar</TabsTrigger>
            <TabsTrigger value="lab" data-testid="legacy-tab-lab">Lab</TabsTrigger>
            <TabsTrigger value="prayer" data-testid="legacy-tab-prayer">Prayer</TabsTrigger>
            <TabsTrigger value="resources" data-testid="legacy-tab-resources">Resources</TabsTrigger>
            <TabsTrigger value="sisters" data-testid="legacy-tab-sisters">Sisters</TabsTrigger>
            <TabsTrigger value="welcome" data-testid="legacy-tab-welcome">Welcome</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="legacy-tab-announcements">News</TabsTrigger>
          </TabsList>

          {/* ============ HOME ============ */}
          <TabsContent value="home" className="space-y-8">
            {/* Welcome message */}
            <Card className="card-base bg-gradient-to-br from-forest/5 to-terracotta/5 border-forest/10">
              <CardContent className="p-6">
                <p className="text-gray-700 leading-relaxed" data-testid="legacy-welcome-message">{settings.welcome_message}</p>
                <p className="text-sm text-forest font-medium mt-3">— Crystal &amp; Dr. Nicole</p>
              </CardContent>
            </Card>

            {/* Next Action: current reflection */}
            <div>
              <h2 className="font-heading text-lg font-semibold text-forest mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-terracotta" /> This Week's Reflection
              </h2>
              <Card className="card-base border-terracotta/20">
                <CardContent className="p-6">
                  {reflection.question ? (
                    <>
                      <p className="text-xs uppercase tracking-widest text-terracotta mb-2">Monday Reflection{reflection.week_of ? ` · Week of ${reflection.week_of}` : ''}</p>
                      <p className="font-heading text-xl text-forest" data-testid="legacy-current-reflection">{reflection.question}</p>
                      <Button size="sm" onClick={() => setSearchParams({ tab: 'reflection' })} className="mt-4 bg-forest hover:bg-forest/90 text-white" data-testid="legacy-respond-reflection-btn">
                        Respond to the Reflection
                      </Button>
                    </>
                  ) : (
                    <p className="text-gray-400 text-sm">This week's reflection will appear here every Monday.</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Live connection points */}
            <div>
              <h2 className="font-heading text-lg font-semibold text-forest mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-terracotta" /> Upcoming Gatherings
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <GatheringCard info={settings.next_gathering} icon={Users} accent="bg-forest" />
                <GatheringCard info={settings.next_witness_circle} icon={Eye} accent="bg-terracotta" />
              </div>
            </div>

            {/* Recent announcements */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-lg font-semibold text-forest flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-terracotta" /> Announcements
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setSearchParams({ tab: 'announcements' })} className="text-forest">
                  View all
                </Button>
              </div>
              {announcements.length > 0 ? (
                <div className="space-y-3">
                  {announcements.slice(0, 2).map(a => (
                    <AnnouncementCard key={a.id} announcement={a} user={user} onUpdate={fetchAnnouncements} />
                  ))}
                </div>
              ) : (
                <Card className="card-base"><CardContent className="p-6 text-center text-gray-400 text-sm">Announcements from Crystal and Dr. Nicole will appear here.</CardContent></Card>
              )}
            </div>

            {/* Community activity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="card-base cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSearchParams({ tab: 'wins' })} data-testid="legacy-home-wins-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-5 w-5 text-terracotta" />
                    <p className="font-heading font-semibold text-forest">Recent Wins</p>
                  </div>
                  {recentWins.length > 0 ? (
                    <div className="space-y-2">
                      {recentWins.slice(0, 3).map(w => (
                        <p key={w.id} className="text-sm text-gray-600 truncate">
                          <span className="font-medium text-forest">{w.author_name}:</span> {w.content || '🎙 shared a voice note'}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Celebrate Wednesday — share the first win of the week.</p>
                  )}
                </CardContent>
              </Card>
              <Card className="card-base cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSearchParams({ tab: 'witness' })} data-testid="legacy-home-witness-card">
                <CardContent className="p-6 text-center">
                  <Eye className="h-6 w-6 text-terracotta mx-auto mb-2" />
                  <p className="font-heading font-semibold text-forest">Witness Wall</p>
                  <p className="text-sm text-gray-400 mt-1">Reflect the gifts, courage, and growth you see in one another — and revisit your own Witness Archive.</p>
                </CardContent>
              </Card>
            </div>

            <p className="text-center text-xs text-gray-400">{home?.member_count || 0} women walking this year together</p>
          </TabsContent>

          {/* ============ WEEKLY RHYTHM CHANNELS ============ */}
          <TabsContent value="reflection">
            <LegacyChannel channel="reflection" user={user} placeholder="Write your honest response... or record it in your own voice." />
          </TabsContent>
          <TabsContent value="wins">
            <LegacyChannel channel="wins" user={user} placeholder="I made the call. I rested without guilt. I said no. What's your win?" />
          </TabsContent>
          <TabsContent value="wisdom">
            <LegacyChannel channel="wisdom" user={user} placeholder="A lesson, a quote, an insight, a gratitude..." />
          </TabsContent>

          {/* ============ PHASE 3: PROJECTS + WITNESS WALL ============ */}
          <TabsContent value="projects">
            <LegacyProjects user={user} />
          </TabsContent>
          <TabsContent value="witness">
            <WitnessWall user={user} />
          </TabsContent>
          <TabsContent value="circle">
            <WitnessCircle user={user} />
          </TabsContent>

          <TabsContent value="calendar">
            <LegacyCalendar />
          </TabsContent>

          <TabsContent value="lab">
            <LegacyLab user={user} />
          </TabsContent>

          <TabsContent value="prayer">
            <LegacyPrayer user={user} />
          </TabsContent>

          <TabsContent value="resources">
            <LegacyResources user={user} />
          </TabsContent>

          <TabsContent value="sisters">
            <LegacySisters user={user} />
          </TabsContent>

          {/* ============ WELCOME ============ */}
          <TabsContent value="welcome" className="space-y-8">
            {/* Welcome video */}
            <Card className="card-base overflow-hidden">
              {settings.welcome_video_url ? (
                <div className="aspect-video bg-black">
                  <iframe
                    src={settings.welcome_video_url}
                    title="Welcome from Crystal and Dr. Nicole"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="legacy-welcome-video"
                  />
                </div>
              ) : (
                <div className="aspect-video bg-gradient-to-br from-forest/10 to-terracotta/10 flex flex-col items-center justify-center" data-testid="legacy-welcome-video-placeholder">
                  <Video className="h-10 w-10 text-forest/40 mb-3" />
                  <p className="font-heading text-forest font-medium">A welcome from Crystal &amp; Dr. Nicole</p>
                  <p className="text-sm text-gray-400 mt-1">Video coming soon</p>
                </div>
              )}
            </Card>

            {/* The experience */}
            <Card className="card-base">
              <CardHeader><CardTitle className="font-heading text-forest">The Legacy Circle Experience</CardTitle></CardHeader>
              <CardContent className="space-y-4 text-gray-700">
                <p className="italic text-forest">{settings.core_promise}</p>
                <p>This is a year-long journey of remembering, becoming, building, and witnessing — held together by two live gatherings each month, gentle weekly rhythms, and a circle of women who will not let you carry your vision alone.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                  <div className="bg-sand/50 rounded-xl p-4">
                    <p className="font-medium text-forest text-sm">Monday Reflection</p>
                    <p className="text-xs text-gray-500 mt-1">One question to pause, tell the truth, and set intention.</p>
                  </div>
                  <div className="bg-sand/50 rounded-xl p-4">
                    <p className="font-medium text-forest text-sm">Celebrate Wednesday</p>
                    <p className="text-xs text-gray-500 mt-1">One win — major or minor. Momentum deserves witnessing.</p>
                  </div>
                  <div className="bg-sand/50 rounded-xl p-4">
                    <p className="font-medium text-forest text-sm">Friday Wisdom</p>
                    <p className="text-xs text-gray-500 mt-1">One lesson, quote, insight, or gratitude to close the week.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Annual journey */}
            <Card className="card-base">
              <CardHeader><CardTitle className="font-heading text-forest flex items-center gap-2"><Compass className="h-5 w-5 text-terracotta" /> The Annual Journey</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {(seasons.length > 0 ? seasons : SEASONS_JOURNEY).map(s => {
                  const num = s.season || s.num;
                  const isCurrent = settings.current_season === num;
                  return (
                    <div key={num} className={`rounded-xl p-4 border ${isCurrent ? 'border-terracotta/40 bg-terracotta/5' : 'border-sand'}`} data-testid={`season-card-${num}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-heading font-semibold text-forest">Season {num}: {s.name}</p>
                        <span className="text-xs text-gray-400">{s.months}</span>
                        {isCurrent && <Badge className="bg-terracotta/10 text-terracotta text-xs">Current Season</Badge>}
                        <a href={`/legacy/seasons?s=${num}`} className="text-xs text-terracotta hover:underline ml-auto" data-testid={`season-enter-${num}`}>
                          Enter the Season →
                        </a>
                      </div>
                      <p className="text-sm text-terracotta italic mt-1">{s.question}</p>
                      <p className="text-sm text-gray-600 mt-1">{s.description}</p>
                      {isCurrent && (s.invitation || s.practices) && (
                        <div className="mt-3 space-y-2">
                          {s.invitation && (
                            <div className="bg-white/70 rounded-lg p-3 border border-terracotta/20">
                              <p className="text-xs uppercase tracking-wide text-terracotta">This season's invitation</p>
                              <p className="text-sm text-gray-700 mt-0.5" data-testid="season-invitation">{s.invitation}</p>
                            </div>
                          )}
                          {s.practices && (
                            <div className="bg-white/70 rounded-lg p-3 border border-sand">
                              <p className="text-xs uppercase tracking-wide text-gray-400">Practices</p>
                              <p className="text-sm text-gray-700 mt-0.5">{s.practices}</p>
                            </div>
                          )}
                          {s.video_url && (
                            <div className="aspect-video bg-black rounded-lg overflow-hidden">
                              <iframe src={s.video_url} title={`Season ${num} video`} className="w-full h-full" allowFullScreen />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Community expectations + how-to */}
            <Card className="card-base">
              <CardHeader><CardTitle className="font-heading text-forest flex items-center gap-2"><BookOpen className="h-5 w-5 text-terracotta" /> How This Space Works</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-3 text-sm text-gray-700 list-none">
                  <li><span className="font-medium text-forest">Weekly rhythm —</span> three light touchpoints each week (Monday, Wednesday, Friday). These are invitations, never assignments.</li>
                  <li><span className="font-medium text-forest">Live gatherings —</span> find dates and Zoom links on the Home page. Recordings will be posted after each session.</li>
                  <li><span className="font-medium text-forest">Announcements —</span> everything important from Crystal and Dr. Nicole lives in the Announcements tab.</li>
                  <li><span className="font-medium text-forest">My Journey —</span> your journey page holds your vision, quarterly milestones, priorities, and progress log across the year. You choose who can see it.</li>
                  <li><span className="font-medium text-forest">Witness Wall —</span> a signature space to reflect the gifts and growth you see in one another. Everything shared about you is kept in your personal Witness Archive.</li>
                  <li><span className="font-medium text-forest">Privacy —</span> everything shared inside the Legacy Circle stays inside the Legacy Circle.</li>
                </ul>
                {settings.covenant_link && (
                  <a href={settings.covenant_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-forest font-medium mt-4 hover:underline" data-testid="legacy-covenant-link">
                    <ExternalLink className="h-4 w-4" /> Read the Founders Covenant (Beloved Community Agreement)
                  </a>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ ANNOUNCEMENTS ============ */}
          <TabsContent value="announcements" className="space-y-4">
            {announcements.length > 0 ? (
              announcements.map(a => (
                <AnnouncementCard key={a.id} announcement={a} user={user} onUpdate={fetchAnnouncements} />
              ))
            ) : (
              <Card className="card-base">
                <CardContent className="p-10 text-center">
                  <Sparkles className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No announcements yet.</p>
                  <p className="text-sm text-gray-400 mt-1">Updates from Crystal and Dr. Nicole will appear here.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
